import React, { forwardRef } from "react";
import "./SearchBar.css";
import searchIcon from "../../assets/images/search-icon.svg";
import closeIcon from "../../assets/images/outline-close-icon.svg";
import { useConstants } from "../../constant/constantProvider";

/**
 * SearchBar — Reusable search input component with search & outline close icons
 *
 * Props:
 *   value       {string}   — current search string
 *   onChange    {function} — callback (newValue: string) => void
 *   placeholder {string}   — input placeholder text (defaults to locale string)
 *   onClear     {function} — optional clear callback
 *   className   {string}   — additional wrapper class
 *   disabled    {boolean}  — disables the input
 */
const SearchBar = forwardRef(({
  value = "",
  onChange,
  placeholder,
  onClear,
  className = "",
  disabled = false,
  ...rest
}, ref) => {
  const { dropdown: t = {} } = useConstants();

  const handleInputChange = (e) => {
    if (disabled) return;
    onChange?.(e.target.value);
  };

  const handleClear = () => {
    if (disabled) return;
    onChange?.("");
    onClear?.();
  };

  const searchPlaceholder = placeholder || t.SEARCH_PLACEHOLDER || "Search by name";

  return (
    <div className={`search-bar-root ${disabled ? "search-bar-disabled" : ""} ${className}`.trim()}>
      <span className="search-bar-icon-wrap">
        <img
          src={searchIcon}
          alt=""
          className="search-bar-icon-img"
          aria-hidden="true"
        />
      </span>
      <input
        ref={ref}
        type="text"
        className="search-bar-input"
        value={value}
        onChange={handleInputChange}
        placeholder={searchPlaceholder}
        aria-label={searchPlaceholder}
        disabled={disabled}
        {...rest}
      />
      {value && !disabled && (
        <button
          type="button"
          className="search-bar-clear-btn"
          onClick={handleClear}
          tabIndex={-1}
          aria-label={t.CLEAR_SEARCH || "Clear search"}
        >
          <img
            src={closeIcon}
            alt=""
            className="search-bar-clear-icon"
            aria-hidden="true"
          />
        </button>
      )}
    </div>
  );
});

SearchBar.displayName = "SearchBar";

export default SearchBar;
